import React, { useEffect, useState } from 'react';
import FileUpload from './components/FileUpload';
import DataVisualizer from './components/DataVisualizer';
import HistorySidebar from './components/HistorySidebar';
import ConfirmationDialog from './components/ConfirmationDialog';
import ComingSoonPanel from './components/pages/ComingSoonPanel';
import AlertsPanel from './components/pages/AlertsPanel';
import ConnectionsPanel from './components/pages/ConnectionsPanel';
import { SavedVisualization } from './utils/storageManager';
import { initializeLLMFromEnv } from './utils/llmProvider';
import { apiService } from './domain/platform/apiService';
import './App.css';

type AppRoute =
  | 'upload'
  | 'dashboard'
  | 'dataset-health'
  | 'saved-views'
  | 'alerts'
  | 'connections'
  | 'settings';

interface ParsedData {
  data: any[];
  headers: string[];
  fileName: string;
  savedId?: string;
}

function App() {
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [datasetId, setDatasetId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [pendingReset, setPendingReset] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [route, setRoute] = useState<AppRoute>('upload');

  useEffect(() => {
    if (parsedData && !parsedData.savedId) {
      setHasUnsavedChanges(true);
    }
  }, [parsedData]);

  useEffect(() => {
    initializeLLMFromEnv();
  }, []);

  const handleDataParsed = async (data: ParsedData) => {
    if (hasUnsavedChanges && parsedData) {
      setShowExitConfirmation(true);
      setPendingReset(false);
      return;
    }
    try {
      const upload = await apiService.uploadDataset(data.fileName, data.headers, data.data);
      const snapshot = await apiService.getDataset(upload.datasetId);

      if (!snapshot) {
        throw new Error('Dataset upload completed but indexing failed. Please retry.');
      }

      setDatasetId(upload.datasetId);
      setParsedData({ data: snapshot.rows, headers: snapshot.headers, fileName: snapshot.fileName });
      setHasUnsavedChanges(true);
      setRoute('dashboard');
    } catch (error: any) {
      console.error('Upload flow failed:', error);
      throw new Error(error?.message || 'Upload failed. Please retry or use a smaller file.');
    }
  };

  const handleReset = () => {
    if (hasUnsavedChanges && parsedData) {
      setShowExitConfirmation(true);
      setPendingReset(true);
      return;
    }
    setParsedData(null);
    setDatasetId(null);
    setHasUnsavedChanges(false);
    setRoute('upload');
  };

  const handleLoadVisualization = async (visualization: SavedVisualization) => {
    if (hasUnsavedChanges && parsedData) {
      setShowExitConfirmation(true);
      return;
    }

    const upload = await apiService.uploadDataset(
      visualization.fileName,
      visualization.headers,
      visualization.data
    );

    setParsedData({
      data: visualization.data,
      headers: visualization.headers,
      fileName: visualization.fileName,
      savedId: visualization.id
    });
    setHasUnsavedChanges(false);
    setIsHistoryOpen(false);
    setRoute('dashboard');
    setDatasetId(upload.datasetId);
  };

  const handleSaved = (savedId: string) => {
    if (parsedData) {
      setParsedData({ ...parsedData, savedId });
      setHasUnsavedChanges(false);
    }
  };

  const handleConfirmExit = () => {
    if (pendingReset) {
      setParsedData(null);
      setDatasetId(null);
      setPendingReset(false);
      setRoute('upload');
    }
    setHasUnsavedChanges(false);
    setShowExitConfirmation(false);
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
    setPendingReset(false);
  };

  const handleDataUpdated = (updatedData: any[]) => {
    if (!parsedData) return;
    setParsedData({ ...parsedData, data: updatedData, savedId: undefined });
    setHasUnsavedChanges(true);
    setRoute('dataset-health');
  };

  const navItems: Array<{ id: AppRoute; label: string; helper: string }> = [
    { id: 'upload', label: 'Upload', helper: 'Import datasets' },
    { id: 'dashboard', label: 'Dashboard', helper: 'Charts and insights' },
    { id: 'dataset-health', label: 'Dataset Health', helper: 'Reliability checks' },
    { id: 'saved-views', label: 'Saved Views', helper: 'Share and history' },
    { id: 'alerts', label: 'Alerts', helper: 'Threshold monitoring' },
    { id: 'connections', label: 'Connections', helper: 'Source integrations' },
    { id: 'settings', label: 'Settings', helper: 'Workspace defaults' }
  ];

  const renderMain = () => {
    if (route === 'upload' || !parsedData) {
      return <FileUpload onDataParsed={handleDataParsed} />;
    }

    if ((route === 'dashboard' || route === 'dataset-health') && datasetId) {
      return (
        <DataVisualizer
          datasetId={datasetId}
          data={parsedData.data}
          headers={parsedData.headers}
          fileName={parsedData.fileName}
          savedId={parsedData.savedId}
          hasUnsavedChanges={hasUnsavedChanges}
          onReset={handleReset}
          onSaved={handleSaved}
          onDataUpdated={handleDataUpdated}
        />
      );
    }

    if (route === 'saved-views') {
      return (
        <ComingSoonPanel
          title="Saved Views"
          description="Shared dashboard links, role-aware access, and dashboard comments are staged here."
        />
      );
    }

    if (route === 'alerts') {
      return <AlertsPanel />;
    }

    if (route === 'connections') {
      return <ConnectionsPanel />;
    }

    if (!datasetId && (route === 'dashboard' || route === 'dataset-health')) {
      return (
        <ComingSoonPanel
          title="Dataset Session Required"
          description="Upload a dataset from the Upload page to initialize API-backed profiling, cleaning, and QA flows."
        />
      );
    }

    return (
      <ComingSoonPanel
        title="Settings"
        description="Manage evidence mode defaults, quality thresholds, and workspace governance settings."
      />
    );
  };

  return (
    <div className="App">
      <aside className="app-sidebar">
        <div className="brand-block">
          <h1>VizData</h1>
          <p>Analytics workspace</p>
        </div>

        <nav className="sidebar-nav" aria-label="Primary navigation">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`sidebar-item ${route === item.id ? 'active' : ''}`}
              onClick={() => setRoute(item.id)}
            >
              <span className="sidebar-item-label">{item.label}</span>
              <span className="sidebar-item-helper">{item.helper}</span>
            </button>
          ))}
        </nav>

        <button className="history-button" onClick={() => setIsHistoryOpen(true)}>
          Open Saved History
        </button>
      </aside>

      <div className="app-content">
        <header className="top-context-bar">
          <div>
            <h2>{navItems.find((item) => item.id === route)?.label}</h2>
            <p>{parsedData ? `${parsedData.fileName} • ${parsedData.data.length.toLocaleString()} rows` : 'No dataset loaded'}</p>
          </div>
          <div className="context-badges">
            <span className="badge">Insight quality checks on</span>
            <span className={`badge ${hasUnsavedChanges ? 'warning' : 'success'}`}>
              {hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'}
            </span>
          </div>
        </header>

        <main className="app-main">{renderMain()}</main>
      </div>

      <HistorySidebar
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        onLoadVisualization={handleLoadVisualization}
      />

      <ConfirmationDialog
        isOpen={showExitConfirmation}
        title="Unsaved Changes"
        message="You have unsaved changes that will be lost. Are you sure you want to continue?"
        confirmText="Discard Changes"
        cancelText="Keep Editing"
        type="warning"
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
      />
    </div>
  );
}

export default App;
